import React from "react";
import _ from "lodash";
import moment from "moment";
import { useMutation, useQueryClient } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AllNflGamesHelper from "../../classes/allNflGamesHelper";
import WaiversPlayersHelper from "../../classes/waiversPlayersHelper";
import useAllPlayers from "../../hooks/queryResults/useAllPlayers";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import useLeagueSeason from "../../hooks/queryResults/useLeagueSeason";
import useNflGamesForWeek from "../../hooks/queryResults/useNflGamesForWeek";
import useRosters from "../../hooks/queryResults/useRosters";
import useUserTeam from "../../hooks/queryResults/useUserTeam";
import useWaiversPlayers from "../../hooks/queryResults/useWaiversPlayers";
import useWaiversRequests from "../../hooks/queryResults/useWaiversRequests";
import useWeek from "../../hooks/queryResults/useWeek";
import useAdminMode from "../../hooks/useAdminMode";
import useCurrentUser from "../../hooks/useCurrentUser";
import useUrls from "../../hooks/useUrls";
import http from "../../services/httpService";
import Util from "../../util/util";
import Box from "../layout/Box";
import PlayerImageByPos from "../player/PlayerImageByPos";
import { getPlayerName } from "../player/PlayerName";
import Loading from "../ui/Loading";
import { animateScroll as scroll } from "react-scroll";

export default function TeamRosterMove({
  team,
  playersIn,
  playersOut,
  setPlayersIn,
  setPlayersOut,
}) {
  const { league: leagueUrl, season: seasonUrl } = useParams();
  const history = useHistory();
  const { leagueSeason, leagueQuery } = useLeagueSeason();
  const { rostersData, rostersQuery } = useRosters();
  const { playersData, playersQuery } = useAllPlayers();
  const { waiversPlayersData, waiversPlayersQuery } = useWaiversPlayers();
  const { currentWeek } = useWeek();
  const { nflGamesData, nflGamesQuery } = useNflGamesForWeek(
    leagueSeason?.obj.season,
    currentWeek
  );
  const { waiversRequestsData, waiversRequestsQuery, waiversRequestsUrl } =
    useWaiversRequests(team?.obj.id);
  const userTeam = useUserTeam();
  const currentUser = useCurrentUser();
  const adminMode = useAdminMode();
  const { apiUrl } = useUrls();
  const currentWeekUrl = Util.weekToUrl(currentWeek);
  const queryClient = useQueryClient();

  const doMoveMutation = useMutation(
    () => {
      const data = { team: team.obj.id };
      if (playersOut.length) data.playerOut = playersOut[0];
      if (playersIn.length) data.playerIn = playersIn[0];

      const url = `${apiUrl}/rosters/move`;
      const config = Util.addTokenToHeaders();
      console.log(`sending POST request to url ${url} with data:`, data);
      return http.post(url, data, config);
    },
    {
      onSuccess: () => {
        console.log("do move mutation - success");
        history.push(
          `/${leagueUrl}/${seasonUrl}/${team.idOrSlug}/${currentWeekUrl}`
        );
      },
      onError: (error) => {
        toast.error(error.message);
        // todo: send sentry error
      },
    }
  );

  const forceMoveMutation = useMutation(
    () => {
      const data = { team: team.obj.id };
      if (playersOut.length) data.playerOut = playersOut[0];
      if (playersIn.length) data.playerIn = playersIn[0];

      const url = `${apiUrl}/rosters/move/force`;
      const config = Util.addTokenToHeaders();
      console.log(`sending POST request to url ${url} with data:`, data);
      return http.post(url, data, config);
    },
    {
      onSuccess: () => {
        console.log("force move mutation - success");
        history.push(
          `/${leagueUrl}/${seasonUrl}/${team.idOrSlug}/${currentWeekUrl}`
        );
      },
      onError: (error) => {
        toast.error(error.message);
        // todo: send sentry error
      },
    }
  );

  const addRequestMutation = useMutation(
    () => {
      const data = { team: team.obj.id };
      if (playersOut.length) data.playerOut = playersOut[0];
      if (playersIn.length) data.playerIn = playersIn[0];

      const url = `${apiUrl}/rosters/requests`;
      const config = Util.addTokenToHeaders();
      console.log(`sending POST request to url ${url} with data:`, data);
      return http.post(url, data, config);
    },
    {
      onSuccess: async () => {
        console.log("add request mutation - success");
        await queryClient.refetchQueries(waiversRequestsUrl);
        toast.success("Successfully added waivers request");
        setPlayersIn([]);
        setPlayersOut([]);
      },
      onError: (error) => {
        toast.error(error.message);
        // todo: send sentry error
        console.error(error);
      },
    }
  );

  const isLoading = useIsLoading(
    leagueSeason,
    leagueQuery,
    rostersData,
    rostersQuery,
    playersData,
    playersQuery,
    userTeam,
    waiversPlayersData,
    waiversPlayersQuery,
    nflGamesData,
    nflGamesQuery,
    team,
    currentUser,
    waiversRequestsData,
    waiversRequestsQuery
  );

  if (isLoading) return <Loading />;

  // isAuthorized
  let isAuthorized =
    (currentUser.isAdmin() && adminMode) || userTeam.obj.id === team.obj.id;
  if (!isAuthorized) {
    history.push(`/${leagueUrl}/${seasonUrl}/${userTeam.idOrSlug}/roster`);
  }

  const nflGamesHelper = new AllNflGamesHelper(nflGamesData);
  const waiversPlayersHelper = new WaiversPlayersHelper(waiversPlayersData);
  const roster = rostersData.rosters.teams[team.obj.id];

  let messages = [];
  let playerMessages = {};
  let canMakeMove = true;
  let isRequest = false;
  let processTime = null;

  if (!(playersIn.length || playersOut.length)) {
    messages.push("Choose a player to add or drop.");
  }

  // isAllowed

  // teamHasMovesAvailable
  const { limitMoves } = leagueSeason.obj.settingsRoster;
  const movesRemaining = team.obj.movesRemaining;
  const teamHasMovesAvailable = !limitMoves || movesRemaining > 0;

  if (!teamHasMovesAvailable) {
    messages.push("Team has no more moves remaining.");
    canMakeMove = false;
  }

  // requiresDrop
  let requiresDrop = false;
  if (playersIn.length) {
    const numOpenSpots = Object.keys(roster.spots)
      .map((num) => roster.spots[num])
      .filter((spot) => !spot.player).length;
    requiresDrop = playersIn.length - playersOut.length > numOpenSpots;
  }

  // any players are on waivers
  const playersInOnWaivers =
    playersIn.length &&
    playersIn.some((playerId) => {
      return waiversPlayersHelper.isOnWaivers(playerId);
    });

  // any drops games have started
  const playersOutGamesHaveStarted =
    playersOut &&
    playersOut.some((playerId) => {
      const player = playersData.players[playerId];
      if (!player.nflTeamAbbr) return false;
      const nflGame = nflGamesHelper.getTeamGame(
        currentWeek,
        player.nflTeamAbbr
      );
      return nflGamesHelper.hasNflGameStarted(nflGame);
    });

  if (playersOutGamesHaveStarted || playersInOnWaivers) {
    isRequest = true;

    // processTime

    if (playersOutGamesHaveStarted) {
      processTime = waiversPlayersData.waivers.dueDate;
      playersOut.forEach((playerId) => {
        const player = playersData.players[playerId];
        if (!player.nflTeamAbbr) return false;
        const nflGame = nflGamesHelper.getTeamGame(
          currentWeek,
          player.nflTeamAbbr
        );
        const hasStarted = nflGamesHelper.hasNflGameStarted(nflGame);
        if (hasStarted) {
          playerMessages[playerId] = "NFL game has already started.";
        } else {
          playerMessages[playerId] = `NFL game starts at ${moment(
            nflGame.startDate
          ).format("ddd, MMM D - h:mm a")} ${moment(
            nflGame.startDate
          ).fromNow()}`;
        }
      });
    }

    if (playersInOnWaivers) {
      playersIn.forEach((playerId) => {
        const clearDate = waiversPlayersHelper.getClearDueDate(playerId);
        const isOn = waiversPlayersHelper.isOnWaivers(playerId);
        playerMessages[playerId] = isOn
          ? "On waivers until " +
            moment(clearDate).format("ddd, MMM D - h:mm a")
          : "Free agent - can be added now";
        if (!processTime || clearDate > processTime) {
          processTime = clearDate;
        }
      });
    }
  }

  if (requiresDrop) {
    messages.push(
      <span style={{ color: "red", fontWeight: "bold" }}>
        A player must be dropped to make room for the player being added.
      </span>
    );
    canMakeMove = false;
  }

  if (canMakeMove && (playersIn.length || playersOut.length)) {
    let processTimeMessage;
    let processTimeFormatted;

    if (processTime) {
      const processTimeMoment = moment(processTime);
      processTimeFormatted = processTimeMoment.format("dddd, MMM D - h:mm a");
    } else {
      processTimeFormatted = "NOW";
    }

    processTimeMessage = isRequest ? (
      "This request will be processed: " + processTimeFormatted
    ) : (
      <>
        This move will be processed:{" "}
        <span style={{ color: "#00ab00" }}>NOW</span>
      </>
    );
    messages.push(processTimeMessage);

    let buttons = [];

    if (adminMode)
      buttons.push(
        <button
          className={"btn btn-alert"}
          style={{ marginRight: "1rem" }}
          onClick={forceMoveMutation.mutate}
        >
          Admin Force Now
        </button>
      );

    if (isRequest) {
      buttons.push(
        <button className={"btn"} onClick={addRequestMutation.mutate}>
          Submit Request
        </button>
      );
    } else {
      buttons.push(
        <button className={"btn"} onClick={doMoveMutation.mutate}>
          Submit
        </button>
      );
    }

    buttons.push(
      <button
        style={{ marginLeft: "1rem" }}
        className={"btn btn-cancel"}
        onClick={() => {
          setPlayersIn([]);
          setPlayersOut([]);
          scroll.scrollToTop({ duration: 250 });
        }}
      >
        Cancel
      </button>
    );

    messages.push(
      <div style={{ width: "100%", textAlign: "center" }}>{buttons}</div>
    );
  }

  const MovePhase = ({ actionType, playerIds }) => {
    return (
      <div
        style={{
          background: "white",
          color: "black",
          padding: "1rem",
          width: "24rem",
          margin: "0 1rem 2rem",
        }}
      >
        <h1
          style={{
            margin: "0 0 1rem",
            padding: "0.5rem",
            textAlign: "center",
            color: "white",
            backgroundColor: actionType === "add" ? "#00ab00" : "red",
          }}
        >
          {_.capitalize(actionType)}
        </h1>
        <div style={{ fontSize: "1.5rem" }}>
          {playerIds.length
            ? playerIds.map((playerId) => {
                const player = playersData.players[playerId];
                return (
                  <PlayerCard
                    key={playerId}
                    player={player}
                    actionType={actionType}
                  />
                );
              })
            : "none"}
        </div>
      </div>
    );
  };

  const PlayerCard = ({ player, actionType }) => {
    const playerName = getPlayerName(player, {
      pos: 1,
      injury: 1,
      nfl: 1,
    });
    return (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <PlayerImageByPos
              player={player}
              greenBg={true}
              style={{ padding: "0.25rem" }}
            />
            <div style={{ marginLeft: "0.25rem" }}>{playerName}</div>
          </div>
          <button
            className={"btn btn-cancel"}
            onClick={() => {
              if (actionType === "add") {
                setPlayersIn([]);
              } else {
                setPlayersOut([]);
              }
            }}
          >
            x
          </button>
        </div>
        {!!playerMessages[player.id] ? (
          <p style={{ margin: 0, opacity: 0.75, fontSize: "0.85rem" }}>
            {playerMessages[player.id]}
          </p>
        ) : null}
      </>
    );
  };

  return (
    <div>
      <Box title={"roster move"}>
        {teamHasMovesAvailable && (playersIn.length || playersOut.length) ? (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexFlow: "row wrap",
              alignItems: "flex-start",
              justifyContent: "space-evenly",
            }}
          >
            <MovePhase actionType={"add"} playerIds={playersIn} />
            <MovePhase actionType={"drop"} playerIds={playersOut} />
          </div>
        ) : null}
        <div
          style={{
            width: "100%",
            padding: "1rem",
            color: "black",
            backgroundColor: "white",
            fontSize: "1rem",
          }}
        >
          {messages.map((message, i) => (
            <div key={i} style={{ margin: "0.25rem 0 1rem" }}>
              {message}
            </div>
          ))}
        </div>
      </Box>
    </div>
  );
}
